import { useState, type ReactNode } from 'react'
import { useQuery } from '@tanstack/react-query'
import { ClipboardCopy, Download, FileText, Link2, Lightbulb, Pencil, Tag } from 'lucide-react'
import { toast } from 'sonner'
import { Button } from '@/components/ui/button'
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { Textarea } from '@/components/ui/textarea'
import { ErrorState, LoadingState } from '@/components/composites/states'
import { ToneBadge } from '@/components/composites/status-badge'
import { usePermission } from '@/features/permissions/usePermission'
import { apiPost } from '@/api/axios-client'
import { getErrorMessage } from '@/api/errors'
import { cn } from '@/lib/utils'
import { FIX_TYPE_LABEL, fetchReviewPack, formatDuration, useDeskAction, useDeskInsights, useSaveThemeFix, type DeskInsights, type FixType, type InsightPeriod, type SupportCase, type ThemeFix } from './api'

/**
 * Insights (below the Support dashboard): what drives tickets, what it costs the customer, and what
 * engineering could fix to make it stop. The numbers come from /support-desk/insights; every ticket's
 * theme comes from the AI or a person and can be corrected on the ticket.
 */

const PERIODS: Array<{ id: InsightPeriod; label: string }> = [
  { id: 'today', label: 'Today' },
  { id: 'week', label: 'This week' },
  { id: 'month', label: 'This month' },
  { id: 'all', label: 'All time' },
]
const FIX_TONE: Record<FixType, 'danger' | 'warning' | 'info' | 'success' | 'brand'> = { bug: 'danger', data: 'warning', config: 'info', howto: 'success', feature: 'brand' }
const FIX_BAR: Record<FixType, string> = { bug: 'bg-danger', data: 'bg-warning', config: 'bg-info', howto: 'bg-success', feature: 'bg-brand' }
const HEADLINE_DOT = { green: 'bg-success', amber: 'bg-warning', red: 'bg-danger' } as const
const hours = (h: number) => (h >= 48 ? `${Math.round(h / 24)} days` : `${h}h`)

function Card({ title, sub, action, children, className }: { title: string; sub?: string; action?: ReactNode; children: ReactNode; className?: string }) {
  return (
    <section className={cn('grid min-w-0 content-start gap-3 rounded-xl border border-border bg-card p-4 shadow-sm', className)}>
      <header className="flex flex-wrap items-start justify-between gap-2">
        <div className="min-w-0">
          <h3 className="text-sm font-semibold">{title}</h3>
          {sub ? <p className="text-xs text-text-secondary">{sub}</p> : null}
        </div>
        {action}
      </header>
      {children}
    </section>
  )
}

export function FixTypeBadge({ fixType }: { fixType: FixType | null }) {
  if (!fixType) return <ToneBadge tone="neutral" size="xs">Not typed</ToneBadge>
  return <ToneBadge tone={FIX_TONE[fixType]} size="xs">{FIX_TYPE_LABEL[fixType]}</ToneBadge>
}

export function InsightsSection({ onOpen }: { onOpen: (caseId: string) => void }) {
  const [period, setPeriod] = useState<InsightPeriod>('week')
  const query = useDeskInsights(period)
  const { can } = usePermission()
  const mayAct = can('update', 'Card')
  const [fixFor, setFixFor] = useState<{ theme: string; fix: ThemeFix | null } | null>(null)
  const [pack, setPack] = useState(false)
  const d = query.data

  return (
    <div className="mt-8 grid gap-4 border-t border-border pt-6">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <p className="text-tiny font-semibold uppercase tracking-wide text-brand">Support dashboard</p>
          <h2 className="text-xl font-semibold">Insights</h2>
          <p className="max-w-3xl text-sm text-text-secondary">What is driving tickets, what it costs the customer, and what engineering could fix to make it stop.</p>
        </div>
        <nav className="flex gap-1 rounded-md bg-muted p-1" aria-label="Insights period">
          {PERIODS.map((p) => (
            <button key={p.id} type="button" onClick={() => setPeriod(p.id)} className={cn('rounded px-3 py-1 text-sm', period === p.id ? 'bg-card font-medium shadow-sm' : 'text-text-secondary hover:bg-card/60')}>
              {p.label}
            </button>
          ))}
        </nav>
      </div>

      {query.isPending ? (
        <LoadingState label="Working out the ticket drivers…" />
      ) : query.error || !d ? (
        <ErrorState error={query.error} onRetry={query.refetch} />
      ) : (
        <>
          <section aria-label="Headlines" className="grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
            {d.headlines.map((h) => (
              <button
                key={h.period}
                type="button"
                onClick={() => setPeriod(h.period)}
                className={cn('grid gap-2 rounded-xl border bg-card p-3 text-left shadow-sm transition-colors', h.period === period ? 'border-brand ring-2 ring-brand/15' : 'border-border hover:border-brand/40')}
              >
                <span className="flex items-center justify-between gap-2 text-tiny font-semibold uppercase tracking-wide text-text-muted">
                  {h.label}
                  <span className={cn('size-2.5 rounded-full', HEADLINE_DOT[h.tone])} aria-hidden="true" />
                </span>
                <span className="text-sm font-medium leading-relaxed">{h.text}</span>
              </button>
            ))}
          </section>

          {d.tagged.total > d.tagged.done ? (
            <p className="text-xs text-text-muted">
              <Tag className="mr-1 inline size-3.5" />
              {d.tagged.done} of {d.tagged.total} tickets in this period have a theme so far; the rest are being tagged a few at a time.
            </p>
          ) : null}

          <Card
            title="Fix these first"
            sub="Themes ranked by customer pain: tickets × priority × hours the customer waited."
            action={<Button size="sm" onClick={() => setPack(true)}><FileText className="size-4" /> Draft developer review pack</Button>}
          >
            {d.fixFirst.length ? (
              <ol className="grid gap-3 md:grid-cols-2 xl:grid-cols-3">
                {d.fixFirst.map((f) => (
                  <li key={f.theme} className="grid content-start gap-2 rounded-lg border border-border-soft p-3">
                    <div className="flex items-center gap-2">
                      <span className="grid size-7 shrink-0 place-items-center rounded-md bg-brand-softer text-sm font-bold text-brand">{f.rank}</span>
                      <span className="font-semibold">{f.theme}</span>
                    </div>
                    <div className="flex flex-wrap gap-1.5">
                      <FixTypeBadge fixType={f.fixType} />
                      {f.product ? <ToneBadge tone="neutral" size="xs">{f.product}</ToneBadge> : null}
                    </div>
                    <dl className="grid grid-cols-3 gap-2">
                      <div><dt className="text-tiny text-text-muted">Tickets</dt><dd className="num text-lg font-semibold">{f.tickets}</dd></div>
                      <div><dt className="text-tiny text-text-muted">Customer waiting</dt><dd className="num text-lg font-semibold">{hours(f.waitedHours)}</dd></div>
                      <div><dt className="text-tiny text-text-muted">Saved a month if fixed</dt><dd className="num text-lg font-semibold text-success">~{hours(f.perMonthHours)}</dd></div>
                    </dl>
                    <p className="text-sm text-text-secondary">{f.proposal}</p>
                    <div className="flex flex-wrap items-center justify-between gap-2 border-t border-border-soft pt-2 text-xs">
                      <span className="text-text-secondary">
                        {f.fix ? (
                          <>Fix: {f.fix.url ? <a className="text-brand hover:underline" href={f.fix.url} target="_blank" rel="noreferrer">{f.fix.label}</a> : f.fix.label} · {f.fix.shippedAt ? `shipped ${new Date(f.fix.shippedAt).toLocaleDateString()}` : 'in progress'}</>
                        ) : 'Not linked to a fix yet'}
                      </span>
                      {mayAct ? (
                        <Button size="sm" variant="ghost" onClick={() => setFixFor({ theme: f.theme, fix: f.fix })}><Link2 className="size-3.5" /> {f.fix ? 'Edit fix' : 'Link a fix'}</Button>
                      ) : null}
                    </div>
                  </li>
                ))}
              </ol>
            ) : (
              <p className="text-sm text-text-muted">No themed tickets in this period yet.</p>
            )}
          </Card>

          <Card title="Recurring themes" sub="Every ticket carries a short theme, suggested by AI and correctable on the ticket. Compared with the period before.">
            <div className="overflow-x-auto rounded-lg border border-border-soft">
              <table className="w-full min-w-[960px] text-sm">
                <thead>
                  <tr className="bg-muted/50 text-left text-tiny uppercase tracking-wide text-text-muted">
                    <th scope="col" className="px-3 py-2 font-semibold">Theme</th>
                    <th scope="col" className="px-3 py-2 font-semibold">Fix type</th>
                    <th scope="col" className="px-3 py-2 text-right font-semibold">Tickets</th>
                    <th scope="col" className="px-3 py-2 font-semibold">Trend</th>
                    <th scope="col" className="px-3 py-2 font-semibold">Clients</th>
                    <th scope="col" className="px-3 py-2 text-right font-semibold">Needed eng.</th>
                    <th scope="col" className="px-3 py-2 text-right font-semibold">Median fix</th>
                    <th scope="col" className="px-3 py-2 font-semibold">Examples</th>
                  </tr>
                </thead>
                <tbody>
                  {d.themes.map((t) => {
                    const delta = t.previous === null ? null : t.count - t.previous
                    return (
                      <tr key={t.theme} className="border-t border-border-soft align-top">
                        <td className="px-3 py-2"><span className="block font-medium">{t.theme}</span><span className="text-xs text-text-muted">{t.product ?? '—'}</span></td>
                        <td className="px-3 py-2"><FixTypeBadge fixType={t.fixType} /></td>
                        <td className="num px-3 py-2 text-right font-semibold">{t.count}</td>
                        <td className={cn('px-3 py-2 text-xs font-semibold', delta === null || delta === 0 ? 'text-text-secondary' : delta > 0 ? 'text-danger' : 'text-success')}>
                          {delta === null ? '—' : delta === 0 ? '— same' : delta > 0 ? `▲ ${delta} more` : `▼ ${-delta} fewer`}
                        </td>
                        <td className="px-3 py-2 text-xs text-text-secondary">{t.clients.join(', ') || '—'}</td>
                        <td className="num px-3 py-2 text-right">{t.engineeringPct === null ? '—' : `${t.engineeringPct}%`}</td>
                        <td className="num px-3 py-2 text-right">{t.medianFixMs === null ? 'open' : formatDuration(t.medianFixMs)}</td>
                        <td className="px-3 py-2 text-xs">
                          {t.examples.map((e, i) => (
                            <span key={e.id}>
                              {i ? ' · ' : ''}
                              <button type="button" className="text-brand hover:underline" title={e.title} onClick={() => onOpen(e.id)}>#{e.reference}</button>
                            </span>
                          ))}
                        </td>
                      </tr>
                    )
                  })}
                  {!d.themes.length ? <tr><td colSpan={8} className="px-3 py-6 text-center text-sm text-text-muted">No tickets in this period.</td></tr> : null}
                </tbody>
              </table>
            </div>
          </Card>

          <div className="grid gap-4 lg:grid-cols-2">
            <Card title="What kind of fix the tickets need" sub="Who should act on the themes, by share of tagged tickets in the period.">
              {d.fixTypes.length ? (
                <>
                  <div className="flex h-3 overflow-hidden rounded-full" role="img" aria-label={d.fixTypes.map((f) => `${f.label} ${f.pct}%`).join(', ')}>
                    {d.fixTypes.map((f) => <span key={f.fixType} className={FIX_BAR[f.fixType]} style={{ width: `${f.pct}%` }} />)}
                  </div>
                  <ul className="grid gap-2">
                    {d.fixTypes.map((f) => (
                      <li key={f.fixType} className="grid grid-cols-[12px_minmax(0,1fr)_auto] items-start gap-2.5">
                        <span className={cn('mt-1 size-3 rounded-sm', FIX_BAR[f.fixType])} aria-hidden="true" />
                        <span><span className="block text-sm font-semibold">{f.label}</span><span className="text-xs text-text-secondary">{f.owner}</span></span>
                        <span className="num text-sm font-semibold">{f.pct}%</span>
                      </li>
                    ))}
                  </ul>
                </>
              ) : (
                <p className="text-sm text-text-muted">No themed tickets in this period yet.</p>
              )}
            </Card>

            <Card title="Did the fix work?" sub="Themes linked to a shipped fix: new tickets a week, four weeks before and after it shipped.">
              {d.impact.length ? (
                <ul className="grid gap-3">
                  {d.impact.map((m) => {
                    const max = Math.max(m.beforePerWeek, m.afterPerWeek, 0.1)
                    return (
                      <li key={m.theme} className="grid gap-2 rounded-lg border border-border-soft p-3">
                        <div className="flex flex-wrap items-baseline justify-between gap-2">
                          <span className="font-semibold">{m.theme}</span>
                          <ToneBadge tone={m.verdict === 'worked' ? 'success' : m.verdict === 'too_early' ? 'neutral' : 'warning'} size="xs">
                            {m.verdict === 'worked' ? `Worked: down ${m.beforePerWeek ? Math.round((1 - m.afterPerWeek / m.beforePerWeek) * 100) : 100}%` : m.verdict === 'too_early' ? 'Too early to tell' : 'No change yet'}
                          </ToneBadge>
                        </div>
                        <span className="text-xs text-text-secondary">Fix: {m.fix.label}{m.fix.shippedAt ? `, shipped ${new Date(m.fix.shippedAt).toLocaleDateString()}` : ''}</span>
                        <div className="grid grid-cols-[56px_minmax(0,1fr)_40px] items-center gap-2 text-xs text-text-secondary">
                          <span>Before</span><span className="h-2 overflow-hidden rounded-full bg-muted"><span className="block h-full rounded-full bg-text-muted" style={{ width: `${(m.beforePerWeek / max) * 100}%` }} /></span><span className="num text-right font-semibold text-foreground">{m.beforePerWeek}</span>
                          <span>After</span><span className="h-2 overflow-hidden rounded-full bg-muted"><span className={cn('block h-full rounded-full', m.verdict === 'worked' ? 'bg-success' : 'bg-warning')} style={{ width: `${(m.afterPerWeek / max) * 100}%` }} /></span><span className="num text-right font-semibold text-foreground">{m.afterPerWeek}</span>
                        </div>
                      </li>
                    )
                  })}
                </ul>
              ) : (
                <p className="text-sm text-text-muted">No fixes linked and shipped yet. Use <strong>Link a fix</strong> on a theme in Fix these first, and add the date it shipped.</p>
              )}
            </Card>
          </div>

          <Quality d={d} />
        </>
      )}

      {fixFor ? <ThemeFixDialog theme={fixFor.theme} fix={fixFor.fix} onClose={() => setFixFor(null)} /> : null}
      {pack ? <ReviewPackDialog period={period} onClose={() => setPack(false)} /> : null}
    </div>
  )
}

function Quality({ d }: { d: DeskInsights }) {
  const q = d.quality
  const items = [
    {
      value: q.fcrPct == null ? '—' : `${q.fcrPct}%`,
      label: 'First contact resolution',
      action: q.closed ? `${q.reopened ?? 0} of ${q.closed} closed tickets came back (reopened or not resolved). Each reopen counts against it.` : 'No tickets closed in this period yet.',
      warn: q.fcrPct != null && q.fcrPct < 85,
    },
    { value: q.otherCausePct === null ? '—' : `${q.otherCausePct}%`, label: 'Fixed with cause "Other"', action: 'Above 10% the cause breakdown stops being useful. Pick a real cause when marking fixed.', warn: (q.otherCausePct ?? 0) > 10 },
    { value: String(q.noProduct), label: 'New tickets with no product', action: 'Set the product at triage so themes land under the right product.', warn: q.noProduct > 0 },
    { value: `${q.notCode} of ${q.sentToEngineering}`, label: 'Sent to engineering, but not a code issue', action: 'Configuration, user error or client-side causes that helpdesk could resolve: a training topic.', warn: q.sentToEngineering > 0 && q.notCode / q.sentToEngineering > 0.25 },
  ]
  return (
    <Card title="Can we trust these numbers?" sub="Gaps in how tickets are recorded; each one makes the insights above less reliable.">
      <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-4">
        {items.map((i) => (
          <div key={i.label} className={cn('grid gap-1 rounded-lg border p-3', i.warn ? 'border-warning/40 bg-warning-soft' : 'border-border-soft')}>
            <p className="num text-xl font-semibold">{i.value}</p>
            <p className="text-sm font-semibold">{i.label}</p>
            <p className="text-xs text-text-secondary">{i.action}</p>
          </div>
        ))}
      </div>
    </Card>
  )
}

function ThemeFixDialog({ theme, fix, onClose }: { theme: string; fix: ThemeFix | null; onClose: () => void }) {
  const save = useSaveThemeFix()
  const [label, setLabel] = useState(fix?.label ?? '')
  const [url, setUrl] = useState(fix?.url ?? '')
  const [shipped, setShipped] = useState(fix?.shippedAt ? fix.shippedAt.slice(0, 10) : '')
  const submit = (remove = false) =>
    save.mutate(
      remove ? { theme, label: null, url: null, shippedAt: null } : { theme, label: label.trim(), url: url.trim() || null, shippedAt: shipped ? new Date(`${shipped}T12:00:00+08:00`).toISOString() : null },
      { onSuccess: onClose },
    )
  return (
    <Dialog open onOpenChange={(o) => !o && !save.isPending && onClose()}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>Link a fix: {theme}</DialogTitle>
          <DialogDescription>The work item or GitHub issue meant to stop these tickets. Add the date it shipped to see, four weeks later, whether the tickets stopped.</DialogDescription>
        </DialogHeader>
        <div className="grid gap-3">
          <div className="grid gap-1.5"><Label htmlFor="fx-label">Work item or issue</Label><Input id="fx-label" value={label} onChange={(e) => setLabel(e.target.value)} placeholder="TAP-812 Recording upload retry" /></div>
          <div className="grid gap-1.5"><Label htmlFor="fx-url">Link (optional)</Label><Input id="fx-url" value={url} onChange={(e) => setUrl(e.target.value)} placeholder="https://…" /></div>
          <div className="grid gap-1.5"><Label htmlFor="fx-shipped">Shipped on (leave empty while in progress)</Label><Input id="fx-shipped" type="date" value={shipped} onChange={(e) => setShipped(e.target.value)} /></div>
        </div>
        <DialogFooter className="gap-2">
          {fix ? <Button variant="ghost" className="mr-auto text-danger" disabled={save.isPending} onClick={() => submit(true)}>Remove link</Button> : null}
          <Button variant="ghost" onClick={onClose} disabled={save.isPending}>Cancel</Button>
          <Button onClick={() => submit()} disabled={!label.trim() || save.isPending}>Save</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

function ReviewPackDialog({ period, onClose }: { period: InsightPeriod; onClose: () => void }) {
  const drafted = useQuery({ queryKey: ['support-desk', 'review-pack', period], queryFn: () => fetchReviewPack(period), staleTime: 0, gcTime: 0, retry: false })
  const [edited, setEdited] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)
  const state = drafted.data ? { title: drafted.data.title, text: edited ?? drafted.data.text } : null
  const setState = (v: { title: string; text: string }) => setEdited(v.text)
  const copy = () => state && navigator.clipboard.writeText(state.text).then(() => toast.success('Copied'))
  const download = () => {
    if (!state) return
    const url = URL.createObjectURL(new Blob([state.text], { type: 'text/plain;charset=utf-8' }))
    const a = document.createElement('a')
    a.href = url
    a.download = `${state.title.replace(/[^\w\s-]/g, '').trim().replace(/\s+/g, '-')}.txt`
    a.click()
    URL.revokeObjectURL(url)
  }
  const saveToProduct = () => {
    if (!state) return
    setBusy(true)
    apiPost('product/entries', { type: 'doc', title: state.title, body: state.text })
      .then(() => toast.success('Saved to Product docs'))
      .catch((err) => toast.error(getErrorMessage(err)))
      .finally(() => setBusy(false))
  }
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="sm:max-w-3xl">
        <DialogHeader>
          <DialogTitle><Lightbulb className="mr-1 inline size-4 text-brand" /> Developer review pack</DialogTitle>
          <DialogDescription>Drafted from the insights for this period. Edit it, then save it to Product docs or take it to the sync.</DialogDescription>
        </DialogHeader>
        {state ? (
          <Textarea value={state.text} onChange={(e) => setState({ ...state, text: e.target.value })} rows={20} className="font-mono text-xs" />
        ) : drafted.error ? (
          <ErrorState error={drafted.error} onRetry={drafted.refetch} />
        ) : (
          <LoadingState label="Drafting…" />
        )}
        <DialogFooter className="gap-2">
          <Button variant="ghost" onClick={copy} disabled={!state}><ClipboardCopy className="size-4" /> Copy</Button>
          <Button variant="ghost" onClick={download} disabled={!state}><Download className="size-4" /> Download</Button>
          <Button onClick={saveToProduct} disabled={!state || busy}><FileText className="size-4" /> Save to Product docs</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

/** The theme on a ticket, with a way to correct it. */
export function ThemeLine({ c, mayAct }: { c: SupportCase; mayAct: boolean }) {
  const [editing, setEditing] = useState(false)
  return (
    <div className="flex flex-wrap items-center gap-2 text-xs">
      <Tag className="size-3.5 text-text-muted" />
      {c.theme ? (
        <>
          <span className="font-medium">{c.theme.name}</span>
          <FixTypeBadge fixType={c.theme.fixType} />
          <span className="text-text-muted">{c.theme.by === 'ai' ? 'suggested by AI' : `set by ${c.theme.byName}`}</span>
        </>
      ) : (
        <span className="text-text-muted">No theme yet (the AI tags new tickets within a few minutes)</span>
      )}
      {mayAct ? <Button size="sm" variant="ghost" className="h-7 px-2" onClick={() => setEditing(true)}><Pencil className="size-3.5" /> {c.theme ? 'Correct' : 'Set theme'}</Button> : null}
      {editing ? <ThemeDialog c={c} onClose={() => setEditing(false)} /> : null}
    </div>
  )
}

function ThemeDialog({ c, onClose }: { c: SupportCase; onClose: () => void }) {
  const run = useDeskAction()
  const insights = useDeskInsights('all')
  const known = (insights.data?.themes ?? []).map((t) => t.theme).filter((t) => t !== 'Not tagged yet')
  const [name, setName] = useState(c.theme?.name ?? '')
  const [fixType, setFixType] = useState<FixType>(c.theme?.fixType ?? 'bug')
  return (
    <Dialog open onOpenChange={(o) => !o && !run.isPending && onClose()}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>Theme</DialogTitle>
          <DialogDescription>What this ticket is about, as a recurring problem, and the kind of fix that would stop tickets like it. Reuse an existing theme when it fits, so they are counted together.</DialogDescription>
        </DialogHeader>
        <div className="grid gap-3">
          <div className="grid gap-1.5">
            <Label htmlFor="th-name">Theme</Label>
            <Input id="th-name" list="th-known" value={name} onChange={(e) => setName(e.target.value)} placeholder="AIVI result re-run" />
            <datalist id="th-known">{known.map((k) => <option key={k} value={k} />)}</datalist>
          </div>
          <div className="grid gap-1.5">
            <Label>Kind of fix</Label>
            <Select value={fixType} onValueChange={(v) => setFixType(v as FixType)}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>{(Object.keys(FIX_TYPE_LABEL) as FixType[]).map((f) => <SelectItem key={f} value={f}>{FIX_TYPE_LABEL[f]}</SelectItem>)}</SelectContent>
            </Select>
          </div>
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={onClose} disabled={run.isPending}>Cancel</Button>
          <Button disabled={name.trim().length < 2 || run.isPending} onClick={() => run.mutate({ id: c.id, action: 'theme', body: { name: name.trim(), fixType } }, { onSuccess: onClose })}>Save</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
